import React from "react";
import { connect } from "react-redux";
import agent from "../agent";
import { Content } from "../widgets/components/Content";
import { Footer } from "../widgets/components/Footer";
import { ScrollTop } from "../widgets/components/ScrollTop";
import SecureVideoPlayer from "./SecureVideoPlayer";

import { LOAD_MEETING } from "../constants/actionTypes";

const mapStateToProps = (state) => ({
  ...state.common,
  currentUser: state.common.currentUser,
  meeting: state.meeting.meeting,
});

const mapDispatchToProps = (dispatch) => ({
  loadMeeting: (id) =>
    dispatch({ type: LOAD_MEETING, payload: agent.Meetings.loadMeeting(id) }),
});

function handleRightClick(event) {
  event.preventDefault();
}

class MeetingVideo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      id: props.match?.params?.id ? props.match.params.id : 1,
      useSecurePlayer: true, // Start with secure player
      securePlayerError: null, // Track if secure player fails
    };
  }

  componentDidMount() {
    this.props.loadMeeting(this.state.id);
    this.handleUserAction({
      type: "load",
      action: "Meeting Window Video Frame",
    });
  }

  componentWillUnmount() {}

  componentDidUpdate(prevProps) {}

  async handleUserAction(submitData) {
    await agent.Frontend.sendUserAction(submitData);
  }

  // Handle secure player error by falling back to legacy
  handleSecurePlayerError = (error) => {
    console.warn("Secure video player failed, falling back to legacy:", error);
    this.setState({
      useSecurePlayer: false,
      securePlayerError: error,
    });
  };

  // Extract video ID from Vimeo URL
  extractVideoId(url) {
    if (!url) return null;

    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /vimeo\.com\/channels\/[^\/]+\/(\d+)/,
      /vimeo\.com\/groups\/[^\/]+\/videos\/(\d+)/,
      /vimeo\.com\/album\/\d+\/video\/(\d+)/,
      /vimeo\.com\/(\d+)\?/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    // If it's already just a number, return it
    if (/^\d+$/.test(url)) {
      return url;
    }

    return null;
  }

  render() {
    const videoId = this.extractVideoId(
      this.props.meeting.meeting_video_source
    );

    // Use legacy player if:
    // 1. No video ID can be extracted, OR
    // 2. use_legacy_player is set, OR
    // 3. Secure player failed and we need fallback
    const useLegacyPlayer =
      !videoId ||
      this.props.meeting.use_legacy_player ||
      !this.state.useSecurePlayer;

    // Fallback to old iframe method if no video ID can be extracted. No
    // hardcoded default here, an unset source means there is nothing to show.
    let meeting_url = this.props.meeting.meeting_video_source || "";

    // The admin field accepts a bare video ID or a URL. Either one without a
    // scheme is a relative path to the browser, so src="123456789" silently
    // resolves against the portal and loads nothing. Build a real embed URL
    // from a bare ID, otherwise just add the missing scheme.
    if (meeting_url && !/^https?:\/\//i.test(meeting_url)) {
      meeting_url = /^\d+$/.test(meeting_url)
        ? `https://player.vimeo.com/video/${meeting_url}`
        : `https://${meeting_url}`;
    }

    if (meeting_url && meeting_url.includes('vimeo.com/event/')) {
      meeting_url = meeting_url.replace(/vimeo\.com\/event\/(\d+)\/([a-zA-Z0-9]+)/, 'vimeo.com/event/$1/embed/$2');
    }

    // The stream switch applies to admins too, so "off" means off everywhere
    // rather than leaving the portal and the admin view disagreeing.
    const streamAvailable =
      (this.props.meeting.video_active ?? 1) === 1 && !!meeting_url;
    const meetingOpen =
      this.props.meeting.active === 1 ||
      this.props.currentUser.role === "admin";

    return (
      <>
        <div
          className="d-flex flex-column flex-root"
          onContextMenu={handleRightClick}
        >
          <div className="page d-flex flex-row flex-column-fluid">
            <div
              className="px-0 d-flex flex-column flex-row-fluid"
              id="kt_wrapper"
            >
              <div
                id="kt_content"
                className="content d-flex flex-column flex-column-fluid home"
              >
                <Content style={{ padding: "0px", maxWidth: "1600px" }}>
                  <>
                    {meetingOpen && streamAvailable ? (
                      <div className="text-center">
                        <div className="box-responsive">
                          {!useLegacyPlayer && videoId ? (
                            // Use new secure video player
                            <SecureVideoPlayer
                              videoId={videoId}
                              onLoad={() =>
                                this.handleUserAction({
                                  type: "load",
                                  action: "Secure Video Player Loaded",
                                })
                              }
                              onError={this.handleSecurePlayerError}
                            />
                          ) : (
                            // Fallback to legacy iframe player
                            <>
                              {this.state.securePlayerError && (
                                <div className="alert alert-warning mb-3">
                                  <small>
                                    <i className="bi bi-info-circle me-1"></i>
                                    Falling back to legacy player.
                                    {this.state.securePlayerError.includes(
                                      "OAuth"
                                    ) &&
                                      " Connect your Vimeo account in Admin → Video Analytics for enhanced features."}
                                  </small>
                                </div>
                              )}
                              <iframe
                                src={meeting_url}
                                width="640"
                                height="360"
                                frameBorder="0"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen=""
                                data-ready="true"
                                style={{ width: "100%", height: "100%" }}
                                onLoad={() =>
                                  this.handleUserAction({
                                    type: "load",
                                    action: "Legacy Meeting Video Frame",
                                  })
                                }
                              ></iframe>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="alert alert-warning">
                          {meetingOpen ? (
                            <>
                              <h5>Live Stream Unavailable</h5>
                              <p>
                                The live stream is not currently available.
                                Please check back later or return to the
                                portal.
                              </p>
                            </>
                          ) : (
                            <>
                              <h5>Meeting Not Active</h5>
                              <p>
                                This meeting is not currently active. Please
                                check back when the meeting starts.
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                </Content>
              </div>
              <Footer />
            </div>
          </div>
        </div>
        <ScrollTop />
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MeetingVideo);
